/**
 * Plasma AI Component
 *
 * Main entry point that conditionally renders either the floating button
 * or the sidebar based on user preferences.
 *
 * This component should be placed in the authenticated layout.
 *
 * V4: Now includes context tracking for page/entity awareness.
 */

import { useEffect, useRef, useState } from "react";

import { logger, PortalEvents } from "~/lib/logger";

import { AISidebar } from "./ai-sidebar";
import { FloatingAIButton } from "./floating-ai-button";
import { useAgentContext } from "./hooks/use-agent-context";
import { useAIPreferences } from "./hooks/use-ai-preferences";

interface PlasmaAIProps {
  /** Children to render (main content) - only used in sidebar mode */
  children?: React.ReactNode;
}

/**
 * PlasmaAI renders the appropriate AI interface based on user preferences.
 *
 * - Floating mode: Renders a floating button in bottom-right corner
 * - Sidebar mode: Renders a toggleable sidebar on the right
 *
 * The preference is stored in Convex and syncs across devices.
 */
export function PlasmaAI({ children }: PlasmaAIProps) {
  const { preferences, isLoading } = useAIPreferences();
  // Default to open in sidebar mode
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const previousModeRef = useRef<string | null>(null);

  // Auto-open sidebar when switching to sidebar mode
  useEffect(() => {
    if (!isLoading && preferences.preferredMode === "sidebar") {
      setSidebarOpen(true);
    }
  }, [isLoading, preferences.preferredMode]);

  // Log AI mode changes
  useEffect(() => {
    if (
      !isLoading &&
      previousModeRef.current !== null &&
      previousModeRef.current !== preferences.preferredMode
    ) {
      logger.info(PortalEvents.AI_MODE_CHANGED, {
        previous_mode: previousModeRef.current,
        current_mode: preferences.preferredMode,
      });
    }
    if (!isLoading) {
      previousModeRef.current = preferences.preferredMode;
    }
  }, [isLoading, preferences.preferredMode]);

  // Don't render anything while loading to prevent flash
  if (isLoading) {
    return <>{children}</>;
  }

  if (preferences.preferredMode === "sidebar") {
    return (
      <>
        {/* Main content - flex-1 to fill available space, margin adjustment when sidebar is open */}
        <div className={`min-w-0 flex-1 ${sidebarOpen ? "mr-[380px]" : ""}`}>
          {children}
        </div>
        <AISidebar isOpen={sidebarOpen} onOpenChange={setSidebarOpen} />
      </>
    );
  }

  // Floating mode
  return (
    <>
      <div className="min-w-0 flex-1">{children}</div>
      <FloatingAIButton />
    </>
  );
}

/**
 * PlasmaAIWithLayout wraps content and adjusts layout for sidebar mode.
 *
 * Use this in the authenticated layout for proper content margin adjustment
 * when the sidebar is open.
 *
 * V4: Includes automatic context tracking for page/entity awareness.
 */
export function PlasmaAIWithLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { preferences, isLoading } = useAIPreferences();
  // Default to open in sidebar mode
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const previousModeRef = useRef<string | null>(null);

  // V4: Track user navigation for context-aware AI responses
  // This runs automatically on every route change
  const { context: _context } = useAgentContext();

  // Auto-open sidebar when switching to sidebar mode
  useEffect(() => {
    if (!isLoading && preferences.preferredMode === "sidebar") {
      setSidebarOpen(true);
    }
  }, [isLoading, preferences.preferredMode]);

  // Log AI mode changes
  useEffect(() => {
    if (
      !isLoading &&
      previousModeRef.current !== null &&
      previousModeRef.current !== preferences.preferredMode
    ) {
      logger.info(PortalEvents.AI_MODE_CHANGED, {
        previous_mode: previousModeRef.current,
        current_mode: preferences.preferredMode,
      });
    }
    if (!isLoading) {
      previousModeRef.current = preferences.preferredMode;
    }
  }, [isLoading, preferences.preferredMode]);

  // Render content without AI during loading
  if (isLoading) {
    return <>{children}</>;
  }

  if (preferences.preferredMode === "sidebar") {
    return (
      <>
        {/* Main content - flex-1 to fill available space, margin adjustment when sidebar is open */}
        <div className={`min-w-0 flex-1 ${sidebarOpen ? "mr-[380px]" : ""}`}>
          {children}
        </div>
        {/* AI Sidebar - fixed on right edge */}
        <AISidebar isOpen={sidebarOpen} onOpenChange={setSidebarOpen} />
      </>
    );
  }

  // Floating mode - just render content + floating button
  return (
    <>
      <div className="min-w-0 flex-1">{children}</div>
      <FloatingAIButton />
    </>
  );
}
